import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { format } from "date-fns";
import { Box, CircularProgress } from "@material-ui/core";
import { toast } from "react-toastify";

import { DatePicker } from "../DatePicker";
import { updateDateSelected, updateTabSelected } from "../../../../store/checkoutPage.slide";
import { RootState } from "../../../../app/rootReducer";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";
import { EventSlotQueryParams } from "../../../../api/EventSlot/EventSlot";
import { useGetEventSlot, useQuoteEventSlots, useScheduleQuote } from "../../../../api/EventSlot/EventSlotApi";
import { useConfig } from "../../../../components/config/ConfigContext";
import { FeatureSchedule } from "../../../../app/consts";
import { fetchQuote } from "../../../../store/QuoteSlice";

export const TabDatePicker = ({ quoteId, hasAddOn }: { quoteId: string; hasAddOn: boolean }) => {
  const { isMoreThanSm } = useBreakpoint();
  const { isFeatureEnabled } = useConfig();
  const dispatch = useDispatch();
  const { date, step } = useSelector((state: RootState) => state.checkout);
  const { quote } = useSelector((state: RootState) => state.quote);

  const [isRender, setRendered] = useState<boolean>(false);
  const [eventSlotQuote] = useState<EventSlotQueryParams>({
    quoteId,
    page: 1,
    pageSize: 1000
  });

  const { data: eventSlots } = useQuoteEventSlots(eventSlotQuote, isFeatureEnabled(FeatureSchedule));
  const { data: selectedEventSlotInfo, isFetching: isFetchingSelected } = useGetEventSlot(quote?.eventSlotId);
  const scheduleQuote = useScheduleQuote();

  useEffect(() => {
    if (step === 0 && selectedEventSlotInfo) {
      if (isRender) return;
      setRendered(true);

      dispatch(updateDateSelected(new Date(selectedEventSlotInfo.startDate).getTime()));
      dispatch(updateTabSelected(hasAddOn ? "add-ons" : "pay-deposit"));
    }
  }, [selectedEventSlotInfo, dispatch, isRender, step, isFetchingSelected, hasAddOn]);

  const events = useMemo(() => {
    if (!eventSlots?.items) return [];
    return eventSlots.items.map((item) => {
      return { date: format(new Date(item.startDate), "yyyy/MM/dd"), title: item.description };
    });
  }, [eventSlots?.items]);

  const compareDate = (dayLeft: Date, dayRight: Date) => {
    return (
      dayLeft.getDate() === dayRight.getDate() &&
      dayLeft.getMonth() === dayRight.getMonth() &&
      dayLeft.getFullYear() === dayRight.getFullYear()
    );
  };

  const onLockEventSlot = async (date: Date): Promise<boolean> => {
    try {
      const eventSlot = eventSlots?.items.find((item) => compareDate(new Date(item.startDate), date));

      if (!quote || !eventSlot) return false;
      if (quote.eventSlotId === eventSlot.id) {
        return false;
      }

      await scheduleQuote.mutateAsync({
        quoteId: quote.id,
        eventSlotId: eventSlot.id
      });
      setRendered(true);
      dispatch(fetchQuote(quoteId));
    } catch (ex: any) {
      toast.error(ex);
      return false;
    }

    return true;
  };

  return (
    <Box width={"100%"}>
      <Box width={"100%"} textAlign={"center"} fontSize={"14px"} color="#4b5462" mb={isMoreThanSm ? "16px" : "6px"}>
        Please select the date you would like your installation to take place
      </Box>
      <Box width={"100%"} padding={isMoreThanSm ? "24px 14px" : 0}>
        <DatePicker
          value={date ? new Date(date) : undefined}
          onChange={async (value) => {
            await onLockEventSlot(value);
            dispatch(updateDateSelected(value.getTime()));
            dispatch(updateTabSelected(hasAddOn ? "add-ons" : "pay-deposit"));
          }}
          events={events}
        />
      </Box>
      {(scheduleQuote.isLoading || isFetchingSelected) && (
        <Box
          position={"absolute"}
          top={0}
          left={0}
          width={"100%"}
          height="100%"
          bgcolor={"rgba(255,255,255,0.5)"}
          display={"flex"}
          alignItems={"center"}
          justifyContent={"center"}
        >
          <CircularProgress color="inherit" />
        </Box>
      )}
    </Box>
  );
};
